import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useCallback, useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { useSendAnalyticsEvent } from "../analytics/events";
import { Room } from "./RoomService";

type DeviceStatus = "disable" | "starting" | "on" | "stopping" | "off";

export type DeviceControl = {
  type: "audio" | "video";
  status: DeviceStatus;
  toggle(): Promise<void>;
  start(deviceId?: MediaDeviceInfo["deviceId"]): Promise<void>;
  stop(): Promise<void>;

  // null value represents the device info is not available yet
  deviceOptions: MediaDeviceInfo[] | null;
  activeDevice: MediaDeviceInfo["deviceId"] | null;
  preferredDevice: MediaDeviceInfo["deviceId"] | null;
};

const preferredDeviceAtom = atomWithStorage("preferredDevice", {
  audio: null,
  video: null,
});

export function useDeviceControl(type: "audio" | "video", room: Room | null) {
  const [status, setStatus] = useState<DeviceStatus>("disable");
  const [activeDevice, setActiveDevice] = useState<
    MediaDeviceInfo["deviceId"] | null
  >(null);
  const [deviceOptions, setDeviceOptions] = useState<MediaDeviceInfo[] | null>(
    null
  );
  const [preferredDevice, setPreferredDevice] = useAtom(preferredDeviceAtom);
  const sendAnalyticsEvent = useSendAnalyticsEvent();
  const { addToast } = useToasts();

  const fetchDeviceOptions = useCallback<
    () => Promise<MediaDeviceInfo[] | null>
  >(
    () =>
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          devices = devices.filter((d) => {
            const kind = type === "audio" ? "audioinput" : "videoinput";
            return d.kind === kind;
          });
          // device label will be blank when user has not grant device
          // permission yet (for privacy reason)
          const hasInfo = devices.every((d) => d.label !== "");
          if (hasInfo) {
            setDeviceOptions(devices);
          }
          return devices;
        })
        .catch((e) => {
          console.error(e);
          return null;
        }),
    [type, setDeviceOptions]
  );

  useEffect(() => {
    // Momplete device information (i.e. device name) is
    // only available after user grant device permission to
    // the site. Here we try to fetch device info when
    // - when user enter the site. The device info would
    //   be avaiable if user has granted permission before
    // - when user just activate the device (with `start()` function)
    //   and grant the permission
    if (!deviceOptions) {
      fetchDeviceOptions();
    }
    if (status == "on" && !deviceOptions) {
      fetchDeviceOptions();
    }

    navigator.mediaDevices.addEventListener("devicechange", fetchDeviceOptions);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        fetchDeviceOptions
      );
    };
  }, [fetchDeviceOptions, type, status, deviceOptions]);

  useEffect(() => {
    if (status === "disable" && room) {
      setStatus("off");
    }
  }, [room, status]);

  /**
   * @param {MediaDeviceInfo["deviceId"]} deviceId? - when undefined,
   *         default device will be used (decide on the browser)
   */
  async function start(deviceId?: MediaDeviceInfo["deviceId"]) {
    if (!room) {
      throw new Error("internal error: cannot start av before joining a room");
    }

    setStatus("starting");
    sendAnalyticsEvent({ name: "audioTurnedOn" });

    try {
      let c: MediaStreamConstraints;
      if (type === "video") {
        c = {
          video: {
            width: 256,
            height: 256,
            frameRate: 10,
            deviceId,
          },
        };
      } else {
        c = {
          audio: { deviceId },
        };
      }

      const stream = await navigator.mediaDevices.getUserMedia(c);
      const tracks =
        type === "audio" ? stream.getAudioTracks() : stream.getVideoTracks();

      if (tracks.length < 0) {
        throw new Error("invalid stream: missing track");
      }

      const track = tracks[0];
      track.addEventListener("ended", () => {
        setStatus("off");
      });

      if (type === "audio") {
        await room.publishAudio(track);
      } else {
        await room.publishVideo(track);
      }

      if (!deviceId) {
        // (browser spec) when request av input without device,
        // the first device will be used by default
        const options = deviceOptions ?? (await fetchDeviceOptions());
        if (options && options.length > 0) {
          deviceId = options[0].deviceId;
        }
      }

      if (deviceId) {
        setActiveDevice(deviceId);
        setPreferredDevice((state) => {
          return { ...state, [type]: deviceId };
        });
      }

      setStatus("on");
    } catch (e) {
      addToast(
        `Unable to activate the device. Please check your browser's audio/video setting and try again.`,
        { appearance: "error" }
      );
      setStatus("off");
    }
  }

  async function stop() {
    if (!room) {
      throw new Error("internal error: cannot start av before joining a room");
    }

    sendAnalyticsEvent({ name: "audioTurnedOff" });
    setStatus("stopping");
    setActiveDevice(null);

    await (type === "audio" ? room.unpublishAudio() : room.unpublishVideo());
    setStatus("off");
  }

  async function toggle() {
    if (status === "on") {
      return stop();
    } else {
      return start();
    }
  }

  useEffect(
    () => () => {
      room?.leave();
    },
    [room]
  );

  return {
    type,
    status,
    toggle,
    start,
    stop,
    deviceOptions,
    activeDevice,
    preferredDevice: preferredDevice[type],
  };
}
